import { Avatar, Divider, Menu, MenuItem, Stack, Typography } from "@mui/material";
import {
  AccountCircle,
  CalendarMonth,
  CalendarMonthOutlined,
  Code,
  Feed,
  Feedback,
  FeedbackOutlined,
  FeedOutlined,
  ListOutlined,
  Logout,
  Person,
  PersonOutlined,
  ResetTv,
  Settings,
  SettingsOutlined,
  SvgIconComponent,
  TvOutlined,
  ViewList,
} from "@mui/icons-material";
import React, { Suspense, useState } from "react";
import { links } from "../../constants";
import { GoogleLogoutButton, MuiNavLink } from "../_Misc";
import { useUserState } from "@services/backend";
import type { UserState } from "src/services/rpc-client";
import { match, useRouteMatch } from "react-router-dom";
import { RedDot } from "./RedDot";
import permissionManager, { Attributes } from "@lib/permission";
import { useAuthContext } from "@src/AuthContext";

const FeedbackDialog = React.lazy(() => import("../_Misc/FeedbackDialog"));

type IMenuItemLink = {
  to?: string;
  name: string;
  Icon: SvgIconComponent;
  ActiveIcon: SvgIconComponent;
  onClick?: () => void;
  hasNew?: (o?: UserState) => boolean;
  /** default is match.isExact */
  // isActive?: NavLinkProps["isActive"];
  isActive?: (match: match | null) => boolean;
  r?: Attributes;
};

/** desktop links are set in desktopMenuItemLinks (in UserMenu component) */
const mobileMenuItemLinks: IMenuItemLink[] = [
  {
    name: "Libraries",
    to: links.folders,
    Icon: TvOutlined,
    ActiveIcon: ResetTv,
    isActive: (m) => Boolean(m?.url.startsWith("/d") && !m.url.startsWith("/d/item/")),
  },
  { name: "Actors", to: links.people, Icon: PersonOutlined, ActiveIcon: Person },
  { name: "Feed", to: links.feed, Icon: FeedOutlined, ActiveIcon: Feed, hasNew: (o) => o?.has_unseen_feed || false },
  { name: "Calendar", to: links.calendar, Icon: CalendarMonthOutlined, ActiveIcon: CalendarMonth },
];

interface Props {
  menuOpen: boolean;
  anchorEl: HTMLElement | null;
  onMenuClose: () => void;
  isMobile?: boolean;
  isMember: boolean;
}

export const UserMenu: React.FC<Props> = ({ anchorEl, menuOpen, onMenuClose, isMobile, isMember }) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const onFeedbackClicked = () => {
    setFeedbackOpen(true);
    onMenuClose();
  };

  const auth = useAuthContext();
  const { data: userState } = useUserState({ enabled: isMember });

  const desktopMenuItemLinks: IMenuItemLink[] = [
    { name: "Settings", to: links.settings, Icon: SettingsOutlined, ActiveIcon: Settings },
    { name: "Lists", to: links.lists, Icon: ListOutlined, ActiveIcon: ViewList },
    { name: "Feedback", onClick: onFeedbackClicked, Icon: FeedbackOutlined, ActiveIcon: Feedback },
    { name: "Manage", to: links.manage, Icon: Code, ActiveIcon: Code, r: "edit_site_data" },
  ];

  const MenuItemLink = ({ link }: { link: IMenuItemLink }) => {
    const { name, onClick, to } = link;
    const match = useRouteMatch(link.to || "");
    const isActive = link.isActive ? link.isActive(match) : match?.isExact;

    const RenderIcon = isActive ? link.ActiveIcon || link.Icon : link.Icon;

    if (link.r && !permissionManager.userCanAccess(link.r)) {
      return <></>;
    }

    const MenuItemElement = (
      <MenuItem onClick={onClick || onMenuClose} sx={{ minHeight: 32 }}>
        <RenderIcon style={{ height: 23, width: 23, marginLeft: 6 }} />
        <span style={{ marginLeft: 8, marginRight: 4 }}>{name}</span>
        {(link.hasNew && link.hasNew(userState) && <RedDot style={{ right: isMobile ? "inherit" : undefined }} />) || ""}
      </MenuItem>
    );

    if (to) {
      return (
        <MuiNavLink to={to} underline="none" color="text.primary">
          {MenuItemElement}
        </MuiNavLink>
      );
    }

    return MenuItemElement;
  };

  return (
    <Menu
      anchorEl={anchorEl}
      keepMounted // if disabled, google iframerpc request won't trigger until render (menu open)
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={menuOpen}
      onClose={onMenuClose}
      sx={{ top: "32px" }}
      // MenuListProps={{ disablePadding: true , sx: { padding: 2 } }}
    >
      <Stack alignItems="center" mb={1}>
        <div>Hi, {auth.name ?? "you"}!</div>
        <Typography variant="subtitle2" color={(t) => t.palette.grey[400]}>
          {auth.email}
        </Typography>
      </Stack>
      <Divider />

      {/* Show the main navigation links (eg libraries, actors, etc) if mobile */}
      {/* NOTE: Menu doesn't accept fragment (it works but MUI will log complains), so we just do 2 isMobile checks. */}
      {isMobile && mobileMenuItemLinks.map((l) => <MenuItemLink key={`um1_${l.name}`} link={l} />)}
      {isMobile && <Divider sx={{ marginTop: 1, marginBottom: 1 }} />}

      {/* Show rest of navigation links */}
      {desktopMenuItemLinks.map((l) => (
        <MenuItemLink key={`um_${l.name}`} link={l} />
      ))}

      <Divider />

      <MenuItem sx={{ minHeight: 32, paddingTop: 0, paddingBottom: 0 }} onClick={onMenuClose}>
        <GoogleLogoutButton Icon={Logout} />
      </MenuItem>

      {/* Feedback dialog */}
      {feedbackOpen && (
        <Suspense fallback="">
          <FeedbackDialog open={feedbackOpen} setOpen={setFeedbackOpen} />
        </Suspense>
      )}
    </Menu>
  );
};

export default UserMenu;
